import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { fetchReport } from "../../../../api/service/report";
import {
  FullReportTitle,
  FullReportAddress,
  ReportStatus,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
} from "../../../../components/dashboard/service/reports/utils";
import {
  CheckBadgeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { ReportVariablesGroupsContent } from "../../../../components/dashboard/service/reports/report-variables-groups-content";
import { ReportMobileNav } from "../../../../components/dashboard/service/reports/report-mobile-nav";
import { ReportDesktopBackButton } from "../../../../components/dashboard/service/reports/report-desktop-back-button";
import { CopyToClipboard } from "../../../../components/utils/Div";
import { mapsServiceUrl, serviceAgreementPath } from "../../../../components/Utils";

export const ReportVariablesPage = () => {
  const { id } = useParams();
  const settings = useSelector((state) => state.session.currentUser.settings);

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);

  // Determine if user can edit this report
  const canEdit =
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  // Load report
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setError(false);
    setErrors([]);

    const params = {
      fields: "id,edit_version,report_number,report_type.*,customer.*,project.*,delivery_address,address,agreement.*,user_can_edit_with_rights,user_can_edit,status,must_be_rectified,variables,created_at,uses_legacy_variables",
    };

    fetchReport(id, params)
      .then((data) => {
        setReport(data?.data);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error("Kunne ikke laste rapport");
        setError(true);
        setIsLoading(false);
      });
  }, [id]);

  const handleOnRefresh = async (updatedReport) => {
    if (updatedReport) {
      setReport((prevReport) => ({
        ...prevReport,
        ...updatedReport,
      }));
    } else {
      // Reload report data
      try {
        const params = {
          fields: "id,edit_version,report_number,report_type.*,customer.*,project.*,delivery_address,address,agreement.*,user_can_edit_with_rights,user_can_edit,status,must_be_rectified,variables,created_at,uses_legacy_variables",
        };
        const data = await fetchReport(id, params);
        setReport(data?.data);
      } catch (error) {
        console.error("Error refreshing report:", error);
        toast.error("Kunne ikke oppdatere rapport");
      }
    }
  };

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!report?.id) {
    return <ResourceEmpty />;
  }

  const headerMeta = [
    {
      component: <ReportStatus report={report} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      component: <ReportAppointmentApprovedStatus report={report} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["new", "ongoing"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      component: <ReportMustBeRectified rectify={report?.must_be_rectified} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["accomplished"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      label: report?.customer?.customer_number + " " + report?.customer?.name,
      description: "",
      path: "/dashboard/customers/" + report?.customer?.id,
      action: null,
      icon: UsersIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.id ? false : true,
      copy: false,
    },
    {
      label: report?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + report?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: report?.project?.id ? false : true,
      copy: false,
    },
    {
      label: (
        <CopyToClipboard
          path={mapsServiceUrl(
            FullReportAddress(report),
            settings?.preferred_maps_service
          )}
          content={FullReportAddress(report)}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: MapPinIcon,
      textColor: "text-teal-700",
      hidden: report?.delivery_address || report.address ? false : true,
      copy: true,
    },
    {
      label: (
        <CopyToClipboard
          path={"tel:" + report?.customer?.mobile}
          content={report?.customer?.mobile}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: PhoneIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.mobile ? false : true,
      copy: true,
    },
    {
      label: "Serviceavtale " + report?.agreement?.agreement_number,
      description: "",
      path: serviceAgreementPath({ serviceAgreement: report?.agreement }),
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: report?.agreement?.id ? false : true,
      copy: true,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle="Variabler"
        title={FullReportTitle(report)}
        meta={headerMeta}
      >
        <div className="mx-auto w-full min-w-0 max-w-7xl px-0 py-3 sm:px-4 sm:py-5 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-5">
          {/* Report Info Section */}
          <div className="mb-3 w-full bg-white shadow sm:mb-6 max-sm:rounded-none sm:rounded-lg">
            <div className="border-b border-gray-200 px-3 py-3 sm:px-6 sm:py-4">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-6 w-6 text-teal-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Variabler for rapport
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {report?.report_number} - {report?.report_type?.displayTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variables Content — full width on mobile, minimal horizontal inset */}
          <div className="w-full min-w-0 bg-white shadow max-sm:rounded-none sm:rounded-lg">
            <div className="w-full min-w-0 max-w-none px-3 py-4 sm:px-6 sm:py-6">
              {!canEdit && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 sm:mb-6 sm:p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Kun visning
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Variablene kan ikke redigeres. Du kan se innholdet,
                          men ikke lagre endringer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              <ReportVariablesGroupsContent
                report={report}
                canEdit={canEdit}
                onRefresh={handleOnRefresh}
              />
              

            </div>
          </div>
        </div>
      </DashboardHeader>

      <ReportDesktopBackButton reportId={report?.id} />
      <ReportMobileNav report={report} />
    </>
  );
};

